/* eslint-disable no-unused-expressions,no-plusplus,prefer-destructuring */
/* global G */
import asObject from 'lib/sequence/component/children/asObject'
import { hide, show } from 'lib/sequence/component/state/hidden'
import { CONFIRM, LIST } from 'app/_shared/events/setStepTab'
import { asyncPipeSpread, bulk } from 'lib/util'
import { reset, set } from 'lib/sequence/component/state/value'
import sequenceComponentState from 'lib/sequence/component/state'
import find from 'lib/sequence/component/children/find'
import initSerialField from 'app/_shared/action/partial/initSerialField'
import initSerialImage from 'app/_shared/action/partial/initSerialImage'
import { settings } from '@app/_shared/session'

const {
  unset: clearError,
} = sequenceComponentState('error')

const {
  unset: clearHelperText,
} = sequenceComponentState('helperText')

const {
  set: setAttachment,
  unset: clearAttachment,
} = sequenceComponentState('attachment')

const getAvatar = model => model[G.CHILDREN].product[G.CACHE]?.[0]?.refs?.avatar?.[0]
const clear = component => clearError(component) && clearHelperText(component)
const bulkHide = bulk(hide)

/**
 * Hides the `btnBack` element, which is not used in this wizard.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): *[]}
 */
const hideBackButton = module => (components, ...args) => {
  const { btnBack } = components

  hide(btnBack)

  return [components, ...args]
}

/**
 * Clears the state of elements in the search form if it isn't the currently active tab.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): *[]}
 */
const clearSearchForm = module => (components, ...args) => {
  const { component, form, existingHelp, btnMore } = components
  const { serial: serialField } = asObject(form[G.CHILDREN])
  const stepState = component[G.STATE]
  const { activeTab } = stepState

  if ([LIST, CONFIRM].includes(activeTab)) {
    bulkHide(existingHelp, btnMore)
    clear(serialField)
  }

  return [components, ...args]
}

/**
 * Sets step header values and flags according to the model data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): *[]}
 */
const setStepState = module => (components, ...args) => {
  const model = module[G.MODEL]
  const { component } = components
  const stepState = component[G.STATE]
  const { name, serial, serialDistinction } = model[G.CACHE]?.value || {}

  if (model[G.CACHE]) {
    stepState.title = name
    stepState.subtitle = serialDistinction || serial
    stepState.completed = true
  } else {
    stepState.title = ''
    stepState.subtitle = ''
    stepState.completed = false
    stepState.confirmed = false
  }

  return [components, ...args]
}

/**
 * Sets the device image from the model data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): *[]}
 */
const setDeviceImage = module => (components, ...args) => {
  const model = module[G.MODEL]
  const { deviceImage } = components

  model[G.CACHE]
    ? setAttachment(deviceImage, getAvatar(model))
    : clearAttachment(deviceImage)

  return [components, ...args]
}

/**
 * Sets the information displayed in the 'confirm' step from the model data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): *[]}
 */
const setConfirmationInfo = module => (components, ...args) => {
  const model = module[G.MODEL]
  const { deviceInfo } = components
  const {
    serial: serialText,
    numberCommission: numberCommissionText,
    productName: productNameText,
  } = asObject(deviceInfo[G.CHILDREN])

  if (model[G.CACHE]) {
    const { name, serial, numberCommission } = model[G.CACHE]?.value || {}

    set(serialText, serial)

    name
      ? set(productNameText, name)
        && show(productNameText)
      : reset(productNameText)
        && hide(productNameText)

    numberCommission
      ? set(numberCommissionText, numberCommission)
      && show(numberCommissionText)
      : reset(numberCommissionText)
      && hide(numberCommissionText)
  } else {
    reset(serialText)
    reset(productNameText)
    reset(numberCommissionText)
  }


  return [components, ...args]
}

/**
 * Clears the checked state of the "Add to favorites" check field if the user leaves the
 * confirmation step.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(*, ...[*]): Promise<*[]>}
 */
const initAddToFavorites = module => async (components, ...args) => {
  const stepState = components.component[G.STATE]
  const { addToFavorites } = components

  stepState.activeTab !== CONFIRM
    && delete addToFavorites[G.STATE].checked

  return [components, ...args]
}

/**
 * Device identification step action.
 *
 * Handles the display and status of the step's elements.
 *
 * Lets the user register a device by assigning an organization to it.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*=): function(...[*]): Promise<*[]>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  hideBackButton(module),
  clearSearchForm(module),
  setStepState(module),
  setDeviceImage(module),
  setConfirmationInfo(module),
  initAddToFavorites(module),
  initSerialField(module),
  initSerialImage(module),
)(find(component), ...args)
